import { Account, AuthClient } from '@appcues/auth';
import { Integration } from 'studio5/pages/integrations/integrations.types';
import { User } from 'studio5/types/user.types';
import createFetch from './create-fetch';

// @TODO: Complete type definitions and move to e.g. flow entity module
export enum FlowState {
  Draft = 'DRAFT',
  Archived = 'ARCHIVED',
}

export type FlowStepChild = {
  id: string;
  index: number;
};

export type FlowStep = {
  id: string;
  steps: Record<string, FlowStepChild>;
};

export type Flow = {
  flow: {
    id: string;
    name: string;
    published: boolean;
    state: FlowState;
    steps: Record<string, FlowStep>;
  };
  rule: Record<string, unknown>;
};

export type ExperienceStep = {
  id: string;
  index: number;
  name: string;
  children?: ExperienceStep[];
};

export type Experience = {
  experience: {
    id: string;
    name: string;
    published: boolean;
    state: FlowState;
    steps: ExperienceStep[];
  };
  rule: Record<string, unknown>;
};

export type Nps = {
  nps: {
    id: string;
    name: string;
    [key: string]: any;
  };
  rule: Record<string, unknown>;
};

export type UserAccount = {
  id: string;
  name: string;
};

export type AccountUser = {
  accountId: string;
  accountUserId: string;
  enabled: boolean;
  id: string;
  role: 'ACCOUNT_ADMIN' | 'EDITORS' | 'PUBLISHER';
};

export type AccountUserResponse = {
  user: User;
  accountUser: AccountUser;
};

export type IntegrationsResponse = {
  attributes: {
    integrationId: string;
  };
  createdAt: number;
  createdBy: string;
  isEnabled: boolean;
  type: string;
  updatedAt: number;
  updatedBy: string;
};

async function rebundleAccount(accountId: string) {
  const response = await fetch(
    `${import.meta.env.BUNDLER_URL}/bundle/accounts/${accountId}`,
    {
      method: 'POST',
      mode: 'cors',
    }
  );

  if (response.status !== 200) {
    throw new Error('Bundler notification failed.');
  }
}

export type CustomerApiClient = {
  getAccount(): Promise<Account>;
  getAccountUser(): Promise<AccountUserResponse>;
  getExperiences(): Promise<Experience[]>;
  getNps(): Promise<Nps[]>;
  getFlow(id: string): Promise<Flow>;
  getFlows(): Promise<Flow[]>;
  getUser(): Promise<User>;
  getUserAccounts(): Promise<{ accounts: Record<string, UserAccount> }>;
  uploadSegmentUserIds(): Promise<
    (body: FormData) => Promise<{
      segment: {
        id: string;
        name: string;
      };
    }>
  >;
  getPrismaticAuthToken(): Promise<string>;
  getIntegrations(): Promise<IntegrationsResponse[]>;
  enableIntegration(): (integration: Integration) => Promise<void>;
  disableIntegration(): (integration: Integration) => Promise<void>;
  getConditionsExplanation(conditions: object): Promise<string>;
};

export default function createCustomerApiClient(
  auth: AuthClient,
  apiUrl: string = import.meta.env.CUSTOMER_API_URL
): CustomerApiClient {
  const fetchCustomerAPI = createFetch(`${apiUrl}/v1`, auth);

  return {
    getAccount() {
      return fetchCustomerAPI('');
    },

    getFlows() {
      return fetchCustomerAPI('flows?summary=true');
    },

    getFlow(id) {
      return fetchCustomerAPI(`flows/${id}`);
    },

    getExperiences() {
      return fetchCustomerAPI('experiences?summary=true');
    },

    getNps() {
      return fetchCustomerAPI(`/nps`, { account: true });
    },

    async getUserAccounts() {
      const id = await auth.getUserId();
      return fetchCustomerAPI(`users/${id}/accounts`, { account: false });
    },

    async getAccountUser() {
      const id = await auth.getUserId();
      return fetchCustomerAPI(`users/${id}`, { account: true });
    },

    async getUser() {
      const id = await auth.getUserId();
      return fetchCustomerAPI(`users/${id}`, { account: false });
    },

    async uploadSegmentUserIds() {
      return async (body: FormData) =>
        fetchCustomerAPI('segments/upload', {
          method: 'POST',
          /* This will make the browser set the Content-Type */
          contentType: null,
          form: body as any,
        });
    },

    async getPrismaticAuthToken() {
      const { auth_token: token } = await fetchCustomerAPI(
        '/integrations/prismatic/auth',
        { account: true }
      );
      return token;
    },

    async getIntegrations() {
      return fetchCustomerAPI(`/integrations`, { account: true });
    },

    enableIntegration() {
      return async (integration: Integration) => {
        const userId = await auth.getUserId();
        const accountId = await auth.getAccountId();

        const payload = {
          id: integration.id,
          isEnabled: true,
          integrationId: `${accountId}:${integration.id}`,
          createdBy: userId,
          createdAt: Date.now(),
        };

        await fetchCustomerAPI(`/integrations/${integration.id}`, {
          method: 'PUT',
          body: payload as any,
        });
        await rebundleAccount(accountId);
      };
    },

    disableIntegration() {
      return async (integration: Integration) => {
        const accountId = await auth.getAccountId();

        await fetchCustomerAPI(`/integrations/${integration.id}`, {
          method: 'DELETE',
        });
        await rebundleAccount(accountId);
      };
    },

    async getConditionsExplanation(qualifiedConditions) {
      return fetchCustomerAPI(`/conditions/explain`, {
        method: 'POST',
        account: true,
        responseType: 'text',
        body: { conditions: qualifiedConditions } as any,
      });
    },
  };
}
